import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { capitalizeFirstLetter } from '@Utils/string-helpers';

import { debounce } from './core/bottom-navbar';
import { colors } from './design-system/color-v2';
import { FillPalette, TextPalette } from './design-system/color-v2/types';
import Icon from './design-system/icon';
import Text from './design-system/text-v2';
import { FontType } from './design-system/text-v2/types';

interface Action {
  iconName: string;
  onClick: () => void;
}

const HeaderWidgetNew = ({
  goBack,
  iconName,
  headerTitle,
  actions,
  classes,
  children,
}: {
  goBack?: () => void;
  iconName?: string;
  headerTitle?: string | React.ReactNode;
  actions?: Action[];
  classes?: string;
  children?: React.ReactNode;
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    }, 250);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div>
      <FixedHeaderContainer ref={ref} className={`${classes} ${isScrolled ? 'shadow-lg' : ''}`}>
        <HeaderContainer>
          <div className='flex items-center gap-2'>
            {iconName && <Icon name={iconName} size='s' onClick={goBack} />}
            <Text className='whitespace-nowrap' font={FontType.H4_B} color={TextPalette.DARK_1}>
              {headerTitle}
            </Text>
          </div>
          <div className='flex items-center gap-2 cursor-pointer'>
            {actions && (
              <>
                {actions?.length <= 2 ? (
                  actions?.map((action, index) => (
                    <Icon key={index} name={action.iconName} size='s' onClick={action.onClick} />
                  ))
                ) : (
                  <div className='relative'>
                    <Icon name='more_vert' size='s' onClick={() => setShowDropdown(!showDropdown)} />
                    {showDropdown && (
                      <DropdownMenu>
                        {actions?.map((action, index) => (
                          <DropdownItem
                            key={index}
                            onClick={() => {
                              action.onClick();
                              setShowDropdown(false);
                            }}>
                            <Icon name={action.iconName} size='s' />
                            <Text className='ml-2' color={TextPalette.DARK_1}>
                              {capitalizeFirstLetter(action.iconName)}
                            </Text>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </HeaderContainer>
        {children}
      </FixedHeaderContainer>
      <div
        ref={(spacer) => {
          if (spacer && ref.current) spacer.style.height = `${ref.current.offsetHeight}px`;
        }}
      />
    </div>
  );
};

const FixedHeaderContainer = styled.div.attrs({
  className: 'w-full md:w-128',
})`
  position: fixed;
  top: 0;
  z-index: 10;
  background-color: ${colors.fill[FillPalette.LIGHT_1]};
  & > *:nth-child(3) {
    margin-left: auto;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  padding: 16px 16px;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  background-color: ${colors.fill[FillPalette.LIGHT_1]};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 20;
`;

const DropdownItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: ${colors.fill[FillPalette.LIGHT_2]};
  }
`;

export default HeaderWidgetNew;
